/// <reference types="vite/client" />
import { OidcConfiguration } from '@axa-fr/oidc-client';
import { routes } from '@fly/shared';

const authorityURL = import.meta.env.VITE_OIDC_ISSUER_URI_KEYCLOAK;
const logoutUrl = `${window.location.origin}/fly/api/v1/auth/logout`;
const dashboardURI = `${window.location.origin}${routes.DASHBOARD.ROOT}`;

export const redirectUri = `${logoutUrl}?post_logout_redirect_uri=${authorityURL}/realms/users/protocol/openid-connect/logout?post_logout_redirect_uri=${encodeURIComponent(dashboardURI)}`;

/**
 * OIDC config for @axa-fr/oidc-client against Keycloak.
 *
 * Key design choices (see PR #1367 for full investigation):
 *
 * monitor_session: false
 *   The OIDC spec's check-session iframe polls Keycloak every ~2s.
 *   Cross-origin cookie restrictions cause it to report "changed" while the
 *   SSO session is still perfectly valid → spurious session-lost dialogs.
 *   Library default is false since v6; explicitly set for clarity.
 *   Ref: https://github.com/AxaFrance/oidc-client/issues/796
 *
 * token_renew_mode: 'access_token_invalid'
 *   Only the access-token expiry drives silent renew (not the id-token).
 *   The library default is 'access_token_or_id_token_invalid'.
 *   The previous value ('access_token_or_id_token_in_cookie') was not a
 *   valid enum member and silently fell through to default behavior.
 *
 * refresh_time_before_tokens_expiration_in_second: (library default — 120s with jitter)
 *   Let the library decide when to silently renew.  Overriding to 180 caused
 *   the browser to refresh too early, racing the E2E test framework's
 *   sessionStorage restore and triggering Keycloak's single-use refresh token
 *   rotation error ("Maximum allowed refresh token reuse exceeded").
 *
 * Session-lost handling lives in OidcProviderWithSessionLost.tsx.
 * Login-callback error workarounds (State not valid, authentication_expired,
 * login_required) are documented inline there.
 */
export const oidcConfig: OidcConfiguration = {
  authority: import.meta.env.VITE_OIDC_ISSUER_URI_KEYCLOAK,
  client_id: import.meta.env.VITE_OIDC_CLIENT_ID_KEYCLOAK,
  // Disable the library's loading watchdog (default 30s). The single top-level
  // OidcProvider wraps public signup pages too, where event.name stays '' by
  // design — the watchdog misreads that idle state as "stuck authenticating"
  // and replaces the page with the LoadingTimeout screen. fly-service handles
  // auth failures explicitly (session-lost, login-callback errors), so the
  // watchdog adds no value here.
  loading_timeout_ms: 0,
  redirect_uri: `${window.location.origin}${routes.AUTH.CALLBACK}`,
  silent_redirect_uri: `${window.location.origin}${routes.AUTH.RENEW}`,
  scope: import.meta.env.VITE_OIDC_SCOPES_KEYCLOAK,
  monitor_session: false,
  authority_time_cache_wellknowurl_in_second: 3600,
  preload_user_info: false,
  token_renew_mode: 'access_token_invalid',
  authority_configuration: {
    issuer: `${authorityURL}/realms/users`,
    authorization_endpoint: `${authorityURL}/realms/users/protocol/openid-connect/auth`,
    token_endpoint: `${window.location.origin}/fly/api/v1/auth/token`,
    revocation_endpoint: `${authorityURL}/realms/users/protocol/openid-connect/revoke`,
    userinfo_endpoint: `${authorityURL}/realms/users/protocol/openid-connect/userinfo`,
    end_session_endpoint: logoutUrl,
  },
};
